import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Image,
  Badge,
  Button,
  Modal,
  Tabs,
  Tab,
  InputGroup,
  Form,
  FormControl
} from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

// Import all icons from react-icons/fa
import { 
  FaUserTie, 
  FaGraduationCap, 
  FaUniversity, 
  FaAward, 
  FaCalendarAlt, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaStar, 
  FaSearch, 
  FaFilePdf, 
  FaHeart, 
  FaUsers, 
  FaChalkboardTeacher, 
  FaLaptopHouse, 
  FaUserShield, 
  FaDownload, 
  FaPrint, 
  FaEye,
  FaTimes,
  FaBriefcase,
  FaVenusMars,
  FaIdBadge,
  FaInfoCircle
} from 'react-icons/fa';

const StaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [favorites, setFavorites] = useState([]);
  
  // PDF states
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    teaching: 0,
    nonTeaching: 0,
    administrative: 0,
    active: 0
  });

  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // API configuration
  const API_BASE_URL = BASE_URL;

  // Category options
  const categoryOptions = [
    { value: 'TEACHING STAFF', label: 'Teaching Staff', icon: FaChalkboardTeacher, color: '#4CAF50' },
    { value: 'NON TEACHING STAFF', label: 'Non-Teaching Staff', icon: FaLaptopHouse, color: '#2196F3' },
    { value: 'ADMINSTRATIVE STAFF', label: 'Administrative Staff', icon: FaUserShield, color: '#9C27B0' }
  ];

  // Helper functions
  const getImageUrl = (path) => {
    if (!path) return null;
    return `${UPLOAD_BASE_URL}${path}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return null;
    }
  };

  const getDepartmentName = (departmentId) => {
    if (!departments || departments.length === 0) return 'Not assigned';
    const department = departments.find(d => d.id == departmentId);
    return department ? department.department_name : 'Not assigned';
  };

  const isFavorite = (staffId) => {
    return favorites.includes(staffId);
  };

  const toggleFavorite = (staffId) => {
    setFavorites(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  // Generate HTML content for PDF
  const generateHTMLContent = (staffMember) => {
    const categoryInfo = getCategoryInfo(staffMember.category_type);
    const age = calculateAge(staffMember.dob);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Staff Profile - ${staffMember.name}</title>
        <style>
          @media print {
            @page { margin: 20mm; }
            .no-print { display: none !important; }
            body { padding: 0; }
            .document-link {
              color: #2196F3 !important;
              background: none !important;
              padding: 0 !important;
              text-decoration: underline;
            }
            .action-buttons { display: none !important; }
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
          }
          
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1a237e;
          }
          
          .header h1 {
            color: #1a237e;
            margin: 0 0 5px 0;
            font-size: 28px;
          }
          
          .header h2 {
            color: #283593;
            margin: 0 0 20px 0;
            font-size: 22px;
          }
          
          .badges {
            display: flex;
            justify-content: center;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 15px;
          }
          
          .badge {
            background: #607D8B;
            color: white;
            padding: 5px 15px;
            border-radius: 20px;
            font-size: 12px;
          }
          
          .profile-section {
            display: flex;
            margin-bottom: 30px;
            align-items: flex-start;
            page-break-inside: avoid;
          }
          
          .profile-image {
            flex: 1;
            padding-right: 30px;
          }
          
          .profile-image img {
            width: 180px;
            height: 180px;
            object-fit: cover;
            border-radius: 8px;
            border: 2px solid #ddd;
          }
          
          .profile-info {
            flex: 2;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
          }
          
          td {
            padding: 8px 0;
            border-bottom: 1px solid #eee;
            vertical-align: top;
          }
          
          td:first-child {
            width: 35%;
            font-weight: bold;
            color: #555;
          }
          
          .section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .section h3 {
            color: #1a237e;
            border-bottom: 2px solid #4CAF50;
            padding-bottom: 8px;
            margin-bottom: 15px;
            font-size: 18px;
          }
          
          .qualification-card {
            margin-bottom: 15px;
            padding: 15px;
            border-left: 4px solid #4CAF50;
            background: #f9f9f9;
            page-break-inside: avoid;
          }
          
          .awards-card {
            padding: 20px;
            background: #FFF8E1;
            border-radius: 8px;
            border-left: 4px solid #FF9800;
            page-break-inside: avoid;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
          
          .no-data {
            text-align: center;
            padding: 20px;
            background: #f9f9f9;
            border-radius: 8px;
            color: #999;
          }
          
          .document-link {
            display: inline-block;
            margin-top: 10px;
            padding: 8px 15px;
            background: #2196F3;
            color: white;
            text-decoration: none;
            border-radius: 4px;
            font-size: 14px;
          }
          
          .document-link:hover {
            background: #1976D2;
            text-decoration: underline;
          }
          
          .action-buttons {
            text-align: center;
            margin-top: 30px;
            padding: 20px;
            border-top: 2px solid #eee;
          }
          
          .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 12px 24px;
            margin: 0 8px;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            cursor: pointer;
            text-decoration: none;
            font-family: inherit;
          }
          
          .btn-print {
            background: #2196F3;
            color: white;
          }
          
          .btn-download {
            background: #4CAF50;
            color: white;
          }
          
          .btn-close {
            background: #f44336;
            color: white;
          }
          
          .btn:hover {
            opacity: 0.9;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
          
          .btn-icon {
            margin-right: 8px;
            font-size: 18px;
          }
        </style>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
      </head>
      <body>
        <div class="header">
          <h1>STAFF PROFILE</h1>
          <h2>${staffMember.name}</h2>
          <div class="badges">
            <span class="badge" style="background: ${categoryInfo.color}">${categoryInfo.label}</span>
            <span class="badge" style="background: ${staffMember.status == 1 ? '#4CAF50' : '#9E9E9E'}">
              ${staffMember.status == 1 ? 'ACTIVE' : 'INACTIVE'}
            </span>
            <span class="badge">ID: ${staffMember.staff_code}</span>
          </div>
        </div>
        
        <div class="profile-section">
          <div class="profile-image">
            ${staffMember.profile_photo ? 
              `<img src="${getImageUrl(staffMember.profile_photo)}" alt="${staffMember.name}" />` : 
              `<div style="width: 180px; height: 180px; background: ${categoryInfo.color}; 
                border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 48px;">
                <i class="fas fa-user-tie"></i>
              </div>`
            }
          </div>
          <div class="profile-info">
            <h3 style="color: #1a237e; margin-top: 0; margin-bottom: 10px;">${staffMember.designation}</h3>
            <p style="color: #666; margin-bottom: 20px;">${getDepartmentName(staffMember.department_id)}</p>
            
            <table>
              <tr>
                <td><i class="fas fa-envelope"></i> Email:</td>
                <td>${staffMember.email || 'Not specified'}</td>
              </tr>
              <tr>
                <td><i class="fas fa-phone"></i> Phone:</td>
                <td>${staffMember.phone || 'Not specified'}</td>
              </tr>
              <tr>
                <td><i class="fas fa-birthday-cake"></i> Date of Birth:</td>
                <td>${formatDate(staffMember.dob)} ${age ? `(${age} years)` : ''}</td>
              </tr>
              <tr>
                <td><i class="fas fa-calendar-alt"></i> Join Date:</td>
                <td>${formatDate(staffMember.join_date)}</td>
              </tr>
              <tr>
                <td><i class="fas fa-briefcase"></i> Experience:</td>
                <td>${staffMember.experience_years || 0} years</td>
              </tr>
              <tr>
                <td><i class="fas fa-graduation-cap"></i> Specialization:</td>
                <td>${staffMember.specialization || 'Not specified'}</td>
              </tr>
              <tr>
                <td><i class="fas fa-venus-mars"></i> Gender:</td>
                <td>${staffMember.gender || 'Not specified'}</td>
              </tr>
            </table>
          </div>
        </div>
        
        ${staffMember.address ? `
        <div class="section">
          <h3><i class="fas fa-map-marker-alt"></i> Contact Address</h3>
          <p style="margin: 0; padding: 15px; background: #f5f5f5; border-radius: 6px;">${staffMember.address}</p>
        </div>
        ` : ''}
        
        <div class="section">
          <h3><i class="fas fa-graduation-cap"></i> Qualifications</h3>
          ${staffMember.qualifications && staffMember.qualifications.length > 0 ? 
            staffMember.qualifications.map((qual, index) => `
              <div class="qualification-card">
                <h4 style="color: #2E7D32; margin: 0 0 8px 0; font-size: 16px;">
                  <i class="fas fa-certificate"></i> ${qual.qualification_name}
                </h4>
                <div style="display: flex; justify-content: space-between; color: #666; margin-bottom: 8px; font-size: 14px;">
                  <span><i class="fas fa-university"></i> <strong>Institution:</strong> ${qual.institution || 'Not specified'}</span>
                  ${qual.year_completed ? `<span><i class="fas fa-calendar"></i> <strong>Year:</strong> ${qual.year_completed}</span>` : ''}
                </div>
                ${qual.document_path ? 
                  `<div style="margin-top: 10px;">
                    <a href="${getImageUrl(qual.document_path)}" class="document-link" target="_blank" rel="noopener noreferrer">
                      <i class="fas fa-file-pdf"></i> View/Download Document
                    </a>
                  </div>` : 
                  '<div style="margin-top: 10px; color: #999;"><i class="fas fa-file"></i> No document attached</div>'
                }
              </div>
            `).join('') : 
            `<div class="no-data">
              <p><i class="fas fa-graduation-cap"></i> No academic qualifications have been added for this staff member.</p>
            </div>`
          }
        </div>
        
        ${staffMember.awards_honors ? `
        <div class="section">
          <h3><i class="fas fa-award"></i> Awards & Honors</h3>
          <div class="awards-card">
            ${staffMember.awards_honors.split('\\n').map(line => `<p style="margin-bottom: 10px;"><i class="fas fa-star"></i> ${line}</p>`).join('')}
          </div>
        </div>
        ` : ''}
        
        <div class="footer">
          <p><i class="fas fa-calendar-alt"></i> Generated on ${new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
          <p><i class="fas fa-copyright"></i> ${new Date().getFullYear()} - Institutional Staff Directory</p>
        </div>
        
        <div class="action-buttons no-print">
          <button onclick="window.print()" class="btn btn-print">
            <span class="btn-icon"><i class="fas fa-print"></i></span>
            Print Document
          </button>
          ${staffMember.profile_photo ? `
          <a href="${getImageUrl(staffMember.profile_photo)}" download="${staffMember.name.replace(/\s+/g, '-')}-profile.jpg" class="btn btn-download">
            <span class="btn-icon"><i class="fas fa-download"></i></span>
            Download Photo
          </a>
          ` : ''}
          <button onclick="window.close()" class="btn btn-close">
            <span class="btn-icon"><i class="fas fa-times"></i></span>
            Close Window
          </button>
        </div>
        
        <script>
          // Auto-scroll to content
          window.onload = function() {
            window.scrollTo(0, 0);
            
            // Add click handlers for all document links
            document.querySelectorAll('.document-link').forEach(link => {
              link.addEventListener('click', function(e) {
                if (!confirm('Do you want to open/download this document?')) {
                  e.preventDefault();
                }
              });
            });
          }
          
          // Keyboard shortcuts
          document.addEventListener('keydown', function(e) {
            // Ctrl/Cmd + P to print
            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
              e.preventDefault();
              window.print();
            }
            
            // Escape to close
            if (e.key === 'Escape') {
              if (confirm('Close this window?')) {
                window.close();
              }
            }
          });
        </script>
      </body>
      </html>
    `;
  };

  // View PDF in browser (HTML preview)
  const viewPDFPreview = (staffMember) => {
    setGeneratingPDF(true);
    
    try {
      // Generate HTML content
      const htmlContent = generateHTMLContent(staffMember);
      
      // Create Blob URL
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        alert('Please allow popups to view PDF preview');
        URL.revokeObjectURL(url);
      } else {
        // Focus the new window
        newWindow.focus();
      }
      
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      alert('Failed to generate PDF preview. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Print staff profile
  const printStaffProfile = (staffMember) => {
    setGeneratingPDF(true);
    
    try {
      const htmlContent = generateHTMLContent(staffMember);
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
          setGeneratingPDF(false);
        }, 1000);
      } else {
        alert('Please allow popups to print');
        setGeneratingPDF(false);
      }
    } catch (error) {
      console.error('Error printing:', error);
      alert('Failed to print document. Please try again.');
      setGeneratingPDF(false);
    }
  };

  // Fetch staff data
  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE_URL}/staff.php`);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        const staffData = response.data.data;
        setStaff(staffData);
        setFilteredStaff(staffData);
        
        // Calculate statistics
        const statsData = {
          total: staffData.length,
          teaching: staffData.filter(s => s.category_type === 'TEACHING STAFF').length,
          nonTeaching: staffData.filter(s => s.category_type === 'NON TEACHING STAFF').length,
          administrative: staffData.filter(s => s.category_type === 'ADMINSTRATIVE STAFF').length,
          active: staffData.filter(s => s.status == 1).length
        };
        setStats(statsData);
      } else {
        setError('No staff data available');
      }
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError('Failed to load staff information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/staff.php?departments=true`);
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchDepartments();
  }, []);

  // Filter staff based on search and filter
  useEffect(() => {
    let filtered = staff;
    
    if (searchTerm) {
      filtered = filtered.filter(staffMember =>
        staffMember.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.staff_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(staffMember => staffMember.category_type === categoryFilter);
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(staffMember => staffMember.department_id == departmentFilter);
    }
    
    setFilteredStaff(filtered);
  }, [staff, searchTerm, categoryFilter, departmentFilter]);

  const handleViewDetails = (staffMember) => {
    setSelectedStaff(staffMember);
    setShowModal(true);
    setActiveTab('overview');
  };

  const handleCloseModal = () => {
    setSelectedStaff(null);
    setShowModal(false);
  };

  const getCategoryInfo = (categoryType) => {
    return categoryOptions.find(cat => cat.value === categoryType) || 
           { label: 'Other Staff', icon: FaUserTie, color: '#607D8B' };
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h4 className="mt-4 text-primary">Loading Our Team</h4>
        <p className="text-muted">Getting to know our talented staff members...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <Alert.Heading>Unable to Load Staff Information</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={fetchStaff}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="public-staff-page" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        padding: '80px 0',
        color: 'white'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={12}>
              <Badge bg="light" text="dark" className="mb-4 p-2">
                <FaUserTie className="me-2" /> Meet Our Team
              </Badge>
              <h1 className="display-4 fw-bold mb-4 text-white">
                Our <span style={{ color: '#64b5f6' }}>Talented</span> Staff
              </h1>
              <p className="lead mb-4" style={{ opacity: 0.75 }}>
                Meet the dedicated professionals who make our institution exceptional. 
                From experienced educators to supportive administrative staff, 
                our team is committed to excellence.
              </p>
              
              <div className="d-flex gap-4">
                <div className="text-center">
                  <h2 className="mb-0 text-white">{stats.total}+</h2>
                  <small style={{ opacity: 0.75 }}>Total Staff</small>
                </div>
                <div className="text-center">
                  <h2 className="mb-0 text-white">{stats.teaching}+</h2>
                  <small style={{ opacity: 0.75 }}>Teaching</small>
                </div>
                <div className="text-center">
                  <h2 className="mb-0 text-white">{stats.active}</h2>
                  <small style={{ opacity: 0.75 }}>Active</small>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search and Filter Section */}
        <Card className="border-0 shadow-lg mb-5" style={{ marginTop: '-60px' }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col lg={5}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <FormControl
                    placeholder="Search staff by name, designation, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              
              <Col lg={3}>
                <Form.Select 
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.department_name}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col lg={4}>
                <div className="d-flex gap-2">
                  <Button
                    variant={categoryFilter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setCategoryFilter('all')}
                    className="flex-fill"
                  >
                    <FaUsers className="me-2" />
                    All
                  </Button>
                  {categoryOptions.map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <Button
                        key={cat.value}
                        variant={categoryFilter === cat.value ? 'primary' : 'outline-primary'}
                        onClick={() => setCategoryFilter(cat.value)}
                        className="flex-fill"
                        style={categoryFilter === cat.value ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
                      >
                        <IconComponent className="me-2" />
                        <span className="d-none d-md-inline">{cat.label}</span>
                      </Button>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Staff Grid */}
        <Row className="g-4">
          {filteredStaff.map((staffMember) => {
            const categoryInfo = getCategoryInfo(staffMember.category_type);
            const age = calculateAge(staffMember.dob);
            const IconComponent = categoryInfo.icon;
            
            return (
              <Col lg={4} md={6} key={staffMember.id}>
                <Card className="border-0 shadow-sm h-100 staff-card">
                  <Card.Body className="p-0 d-flex flex-column">
                    {/* Profile Image - Fixed height removed */}
                    <div className="position-relative" style={{ height: '400px', overflow: 'hidden' }}>
                      {staffMember.profile_photo ? (
                        <Image
                          src={getImageUrl(staffMember.profile_photo)}
                          alt={staffMember.name}
                          fluid
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${staffMember.name}&background=${categoryInfo.color.replace('#', '')}&color=fff&size=400`;
                          }}
                        />
                      ) : (
                        <div 
                          className="d-flex align-items-center justify-content-center h-100"
                          style={{ 
                            background: `linear-gradient(135deg, ${categoryInfo.color}, ${categoryInfo.color}99)`,
                            color: 'white'
                          }}
                        >
                          <FaUserTie size={64} />
                        </div>
                      )}
                      
                      {/* Favorite Button */}
                      <Button
                        variant="light"
                        size="sm"
                        className="position-absolute top-0 end-0 m-3 rounded-circle"
                        style={{ 
                          width: '40px', 
                          height: '40px',
                          backdropFilter: 'blur(4px)',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        }}
                        onClick={() => toggleFavorite(staffMember.id)}
                      >
                        <FaHeart className={isFavorite(staffMember.id) ? 'text-danger' : 'text-muted'} />
                      </Button>
                      
                      {/* Category Badge */}
                      <Badge 
                        className="position-absolute top-0 start-0 m-3 d-flex align-items-center"
                        style={{ 
                          background: categoryInfo.color,
                          backdropFilter: 'blur(4px)',
                          backgroundColor: `${categoryInfo.color}CC`
                        }}
                      >
                        <IconComponent className="me-1" />
                        <span className="d-none d-sm-inline">{categoryInfo.label}</span>
                      </Badge>
                      
                      {/* Status Badge */}
                      <Badge
                        className="position-absolute bottom-0 end-0 m-3"
                        bg={staffMember.status == 1 ? 'success' : 'secondary'}
                      >
                        {staffMember.status == 1 ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    {/* Staff Info */}
                    <div className="p-4 flex-grow-1 d-flex flex-column">
                      <div className="mb-3 flex-grow-1">
                        <h5 className="mb-1 fw-bold">{staffMember.name}</h5>
                        <p className="text-primary mb-2 fw-semibold">{staffMember.designation}</p>
                        
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">
                            <FaUniversity className="me-1" />
                            Department
                          </small>
                          <div className="text-truncate">{getDepartmentName(staffMember.department_id)}</div>
                        </div>
                        
                        {/* Quick Info */}
                        <div className="row g-2 mb-3">
                          <div className="col-6">
                            <small className="text-muted d-block">
                              <FaBriefcase className="me-1" />
                              Experience
                            </small>
                            <div>{staffMember.experience_years || 0} years</div>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">
                              <FaIdBadge className="me-1" />
                              Staff ID
                            </small>
                            <div className="text-truncate">{staffMember.staff_code}</div>
                          </div>
                        </div>
                        
                        {/* Contact Info */}
                        <div className="mt-auto">
                          {staffMember.email && (
                            <div className="d-flex align-items-center mb-2">
                              <FaEnvelope className="me-2 text-muted" size={14} />
                              <small className="text-truncate">{staffMember.email}</small>
                            </div>
                          )}
                          {staffMember.phone && (
                            <div className="d-flex align-items-center">
                              <FaPhone className="me-2 text-muted" size={14} />
                              <small>{staffMember.phone}</small>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          className="flex-fill"
                          onClick={() => handleViewDetails(staffMember)}
                        >
                          <FaEye className="me-1" />
                          View Profile
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => toggleFavorite(staffMember.id)}
                          title={isFavorite(staffMember.id) ? 'Remove from favorites' : 'Add to favorites'}
                          className="px-3"
                        >
                          <FaHeart className={isFavorite(staffMember.id) ? 'text-danger' : ''} />
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* No Results */}
        {filteredStaff.length === 0 && (
          <div className="text-center py-5">
            <FaSearch size={48} className="text-muted mb-3" />
            <h4 className="mb-3">No Staff Members Found</h4>
            <p className="text-muted mb-4">
              {searchTerm 
                ? `No staff members match "${searchTerm}"`
                : "No staff members are currently available"}
            </p>
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setDepartmentFilter('all');
              }}
            >
              Show All Staff
            </Button>
          </div>
        )}

        {/* Stats Section */}
        <Row className="mt-5 pt-5">
          <Col lg={12}>
            <div className="section-header text-center mb-5">
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaUsers className="me-2" /> Staff Statistics
              </Badge>
              <h2 className="mb-3">Our Team By Numbers</h2>
              <p className="text-muted">A diverse and experienced team dedicated to excellence</p>
            </div>
            
            <Row className="g-4">
              <Col lg={3} md={6}>
                <Card className="border-0 shadow-sm text-center py-4 hover-lift">
                  <div className="mb-3">
                    <div className="mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        width: '70px',
                        height: '70px',
                        background: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '28px'
                      }}>
                      <FaChalkboardTeacher />
                    </div>
                  </div>
                  <h2 className="mb-0">{stats.teaching}</h2>
                  <p className="text-muted mb-0">Teaching Staff</p>
                </Card>
              </Col>
              
              <Col lg={3} md={6}>
                <Card className="border-0 shadow-sm text-center py-4 hover-lift">
                  <div className="mb-3">
                    <div className="mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        width: '70px',
                        height: '70px',
                        background: 'linear-gradient(135deg, #2196F3, #03A9F4)',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '28px'
                      }}>
                      <FaLaptopHouse />
                    </div>
                  </div>
                  <h2 className="mb-0">{stats.nonTeaching}</h2>
                  <p className="text-muted mb-0">Non-Teaching Staff</p>
                </Card>
              </Col>
              
              <Col lg={3} md={6}>
                <Card className="border-0 shadow-sm text-center py-4 hover-lift">
                  <div className="mb-3">
                    <div className="mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        width: '70px',
                        height: '70px',
                        background: 'linear-gradient(135deg, #9C27B0, #E91E63)',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '28px'
                      }}>
                      <FaUserShield />
                    </div>
                  </div>
                  <h2 className="mb-0">{stats.administrative}</h2>
                  <p className="text-muted mb-0">Administrative Staff</p>
                </Card>
              </Col>
              
              <Col lg={3} md={6}>
                <Card className="border-0 shadow-sm text-center py-4 hover-lift">
                  <div className="mb-3">
                    <div className="mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        width: '70px',
                        height: '70px',
                        background: 'linear-gradient(135deg, #FF9800, #FF5722)',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '28px'
                      }}>
                      <FaStar />
                    </div>
                  </div>
                  <h2 className="mb-0">{stats.active}</h2>
                  <p className="text-muted mb-0">Active Members</p>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Staff Detail Modal - FIXED VERSION */}
      {selectedStaff && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="staff-detail-modal"
          scrollable
        >
          <Modal.Header closeButton className="border-bottom-0 pb-0">
            <Modal.Title className="fw-bold">
              <FaUserTie className="me-2 text-primary" />
              Staff Profile
            </Modal.Title>
            <div className="ms-auto d-flex align-items-center gap-2">
              <Badge bg="light" text="dark" className="px-3">
                ID: {selectedStaff.staff_code}
              </Badge>
              <Badge bg={selectedStaff.status == 1 ? 'success' : 'secondary'}>
                {selectedStaff.status == 1 ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </Modal.Header>
          
          <Modal.Body className="p-0">
            {/* Profile Header with responsive image */}
            <div className="position-relative bg-light">
              <div className="row g-0">
                {/* Left side - Photo */}
                <div className="col-md-5 p-4 d-flex align-items-center justify-content-center">
                  <div className="position-relative" style={{ width: '100%', maxWidth: '300px' }}>
                    {selectedStaff.profile_photo ? (
                      <Image
                        src={getImageUrl(selectedStaff.profile_photo)}
                        alt={selectedStaff.name}
                        fluid
                        className="img-fluid rounded shadow"
                        style={{ 
                          width: '100%',
                          aspectRatio: '1/1',
                          objectFit: 'cover',
                          border: '4px solid white'
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${selectedStaff.name}&background=${getCategoryInfo(selectedStaff.category_type).color.replace('#', '')}&color=fff&size=400`;
                        }}
                      />
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center rounded shadow"
                        style={{ 
                          width: '100%',
                          aspectRatio: '1/1',
                          background: `linear-gradient(135deg, ${getCategoryInfo(selectedStaff.category_type).color}, ${getCategoryInfo(selectedStaff.category_type).color}99)`,
                          color: 'white',
                          border: '4px solid white'
                        }}
                      >
                        <FaUserTie size={80} />
                      </div>
                    )}
                    
                    {/* Category Badge on Photo */}
                    <Badge 
                      className="position-absolute bottom-0 start-0 m-3"
                      style={{ 
                        background: getCategoryInfo(selectedStaff.category_type).color,
                        fontSize: '0.85rem'
                      }}
                    >
                      {getCategoryInfo(selectedStaff.category_type).label}
                    </Badge>
                  </div>
                </div>
                
                {/* Right side - Basic Info */}
                <div className="col-md-7 p-4">
                  <div className="h-100 d-flex flex-column justify-content-center">
                    <h3 className="fw-bold mb-2">{selectedStaff.name}</h3>
                    <h5 className="text-primary mb-3">{selectedStaff.designation}</h5>
                    
                    <div className="mb-4">
                      <p className="mb-2">
                        <FaUniversity className="me-2 text-muted" />
                        <strong>Department:</strong> {getDepartmentName(selectedStaff.department_id)}
                      </p>
                      {selectedStaff.specialization && (
                        <p className="mb-2">
                          <FaGraduationCap className="me-2 text-muted" />
                          <strong>Specialization:</strong> {selectedStaff.specialization}
                        </p>
                      )}
                      <p className="mb-0">
                        <FaBriefcase className="me-2 text-muted" />
                        <strong>Experience:</strong> {selectedStaff.experience_years || 0} years
                      </p>
                    </div>
                    
                    {/* Quick Contact Info */}
                    <div className="bg-light rounded p-3">
                      <h6 className="mb-3">
                        <FaInfoCircle className="me-2 text-primary" />
                        Contact Information
                      </h6>
                      <div className="row g-2">
                        {selectedStaff.email && (
                          <div className="col-12">
                            <div className="d-flex align-items-center">
                              <FaEnvelope className="me-2 text-muted" size={14} />
                              <small className="text-truncate">{selectedStaff.email}</small>
                            </div>
                          </div>
                        )}
                        {selectedStaff.phone && (
                          <div className="col-12">
                            <div className="d-flex align-items-center">
                              <FaPhone className="me-2 text-muted" size={14} />
                              <small>{selectedStaff.phone}</small>
                            </div>
                          </div>
                        )}
                        {selectedStaff.dob && (
                          <div className="col-12">
                            <div className="d-flex align-items-center">
                              <FaCalendarAlt className="me-2 text-muted" size={14} />
                              <small>
                                {formatDate(selectedStaff.dob)} 
                                {calculateAge(selectedStaff.dob) && ` (${calculateAge(selectedStaff.dob)} years)`}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tabs Navigation - Better Styling */}
            <div className="border-top">
              <Tabs 
                activeKey={activeTab} 
                onSelect={(k) => setActiveTab(k)}
                className="mb-0 border-bottom-0"
                fill
              >
                <Tab 
                  eventKey="overview" 
                  title={
                    <span className="d-flex align-items-center justify-content-center">
                      <FaUserTie className="me-2" />
                      <span className="d-none d-md-inline">Overview</span>
                    </span>
                  }
                  className="p-4"
                >
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h6 className="card-title text-primary mb-4">
                            <FaBriefcase className="me-2" />
                            Professional Details
                          </h6>
                          <dl className="row mb-0">
                            <dt className="col-sm-5 text-muted">Department</dt>
                            <dd className="col-sm-7">{getDepartmentName(selectedStaff.department_id)}</dd>
                            
                            <dt className="col-sm-5 text-muted">Category</dt>
                            <dd className="col-sm-7">{selectedStaff.category_type}</dd>
                            
                            <dt className="col-sm-5 text-muted">Specialization</dt>
                            <dd className="col-sm-7">{selectedStaff.specialization || 'Not specified'}</dd>
                            
                            <dt className="col-sm-5 text-muted">Experience</dt>
                            <dd className="col-sm-7">{selectedStaff.experience_years || 0} years</dd>
                            
                            <dt className="col-sm-5 text-muted">Join Date</dt>
                            <dd className="col-sm-7">{formatDate(selectedStaff.join_date)}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h6 className="card-title text-primary mb-4">
                            <FaUserTie className="me-2" />
                            Personal Details
                          </h6>
                          <dl className="row mb-0">
                            <dt className="col-sm-5 text-muted">Gender</dt>
                            <dd className="col-sm-7">{selectedStaff.gender || 'Not specified'}</dd>
                            
                            {selectedStaff.dob && (
                              <>
                                <dt className="col-sm-5 text-muted">Date of Birth</dt>
                                <dd className="col-sm-7">{formatDate(selectedStaff.dob)}</dd>
                                
                                {calculateAge(selectedStaff.dob) && (
                                  <>
                                    <dt className="col-sm-5 text-muted">Age</dt>
                                    <dd className="col-sm-7">{calculateAge(selectedStaff.dob)} years</dd>
                                  </>
                                )}
                              </>
                            )}
                            
                            {selectedStaff.address && (
                              <>
                                <dt className="col-sm-5 text-muted">Address</dt>
                                <dd className="col-sm-7">{selectedStaff.address}</dd>
                              </>
                            )}
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab>
                
                <Tab 
                  eventKey="qualifications" 
                  title={
                    <span className="d-flex align-items-center justify-content-center">
                      <FaGraduationCap className="me-2" />
                      <span className="d-none d-md-inline">Qualifications</span>
                    </span>
                  }
                  className="p-4"
                >
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="mb-0 text-primary">
                      <FaGraduationCap className="me-2" />
                      Academic & Professional Qualifications
                    </h6>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => viewPDFPreview(selectedStaff)}
                      disabled={generatingPDF}
                    >
                      <FaFilePdf className="me-1" />
                      {generatingPDF ? 'Generating...' : 'Export PDF'}
                    </Button>
                  </div>
                  
                  {selectedStaff.qualifications && selectedStaff.qualifications.length > 0 ? (
                    <div className="row g-3">
                      {selectedStaff.qualifications.map((qual) => (
                        <div className="col-12" key={qual.id}>
                          <Card className="border-0 shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <h6 className="mb-1 text-primary">
                                    <FaUniversity className="me-2" />
                                    {qual.qualification_name}
                                  </h6>
                                  <p className="mb-1 text-muted">
                                    <FaUniversity className="me-2" />
                                    {qual.institution || 'Institution not specified'}
                                  </p>
                                  {qual.year_completed && (
                                    <p className="mb-0 text-muted">
                                      <FaCalendarAlt className="me-2" />
                                      Completed: {qual.year_completed}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {qual.document_path && (
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    href={getImageUrl(qual.document_path)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FaEye className="me-1" />
                                    View Document
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    href={getImageUrl(qual.document_path)}
                                    download
                                  >
                                    <FaDownload className="me-1" />
                                    Download
                                  </Button>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <FaGraduationCap size={48} className="text-muted mb-3" />
                      <h6>No Qualifications Listed</h6>
                      <p className="text-muted">No academic qualifications have been added for this staff member.</p>
                    </div>
                  )}
                </Tab>
                
                <Tab 
                  eventKey="awards" 
                  title={
                    <span className="d-flex align-items-center justify-content-center">
                      <FaAward className="me-2" />
                      <span className="d-none d-md-inline">Awards</span>
                    </span>
                  }
                  className="p-4"
                >
                  {selectedStaff.awards_honors ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="mb-0 text-primary">
                          <FaAward className="me-2" />
                          Awards, Honors & Achievements
                        </h6>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => viewPDFPreview(selectedStaff)}
                          disabled={generatingPDF}
                        >
                          <FaFilePdf className="me-1" />
                          Export PDF
                        </Button>
                      </div>
                      <Card className="border-0 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center mb-3">
                            <FaAward className="text-warning me-2" size={24} />
                            <h6 className="mb-0">Recognition & Achievements</h6>
                          </div>
                          <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-line' }}>
                            {selectedStaff.awards_honors}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <FaAward size={48} className="text-muted mb-3" />
                      <h6>No Awards Listed</h6>
                      <p className="text-muted">No awards or honors have been added for this staff member.</p>
                    </div>
                  )}
                </Tab>
              </Tabs>
            </div>
          </Modal.Body>
          
          <Modal.Footer className="border-top-0 pt-0">
            <div className="d-flex flex-column flex-md-row w-100 gap-3">
              <div className="d-flex align-items-center">
                <Button
                  variant={isFavorite(selectedStaff.id) ? 'danger' : 'outline-danger'}
                  onClick={() => toggleFavorite(selectedStaff.id)}
                  className="me-2"
                >
                  <FaHeart className="me-2" />
                  {isFavorite(selectedStaff.id) ? 'Remove Favorite' : 'Add Favorite'}
                </Button>
                <small className="text-muted">
                  {isFavorite(selectedStaff.id) ? 'Added to favorites' : 'Add to favorites list'}
                </small>
              </div>
              
              <div className="ms-md-auto d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  onClick={handleCloseModal}
                >
                  <FaTimes className="me-2" />
                  Close
                </Button>
                <Button 
                  variant="success"
                  onClick={() => viewPDFPreview(selectedStaff)}
                  disabled={generatingPDF}
                >
                  <FaFilePdf className="me-2" />
                  {generatingPDF ? 'Generating...' : 'View PDF'}
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => printStaffProfile(selectedStaff)}
                  disabled={generatingPDF}
                >
                  <FaPrint className="me-2" />
                  Print
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      
      {/* Add some custom styles */}
      <style jsx>{`
        .staff-card:hover {
          transform: translateY(-5px);
          transition: transform 0.3s ease;
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        
        .hover-lift:hover {
          transform: translateY(-3px);
          transition: transform 0.2s ease;
        }
        
        .nav-tabs .nav-link {
          border: none !important;
          border-bottom: 3px solid transparent !important;
          color: #6c757d;
          padding: 1rem 1.5rem;
        }
        
        .nav-tabs .nav-link.active {
          border-bottom: 3px solid #0d6efd !important;
          color: #0d6efd;
          background: transparent;
        }
        
        .nav-tabs .nav-link:hover {
          border-bottom: 3px solid #dee2e6 !important;
        }
      `}</style>
    </div>
  );
};

export default StaffPage;